import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { SocialSecurityBenefits as SectionKey } from 'Consts/Questionnaire';
import { section } from 'Consts/Queries';
import { QueryVal } from 'Helpers/Query';
import CircleWithCount from 'Icons/CircleWithCount';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import BoolInput from '../Fields/BoolInput';
import CurrencyInput from '../Fields/CurrencyInput';
import Section from '../Section';
import RadioInput from '../Fields/RadioInput';
import FieldList from '../Fields/FieldList';
import LabeledVerticalExpander from '../UI/LabeledVerticalExpander';
import SelectInput from '../Fields/SelectInput';

const BENEFIT_TYPE = [
  {
    id: 'personal/own',
    text: 'Personal/ Own',
  },
  {
    id: 'survivor/widow(er)',
    text: 'Survivor/ Widow(er)',
  },
  {
    id: 'disability',
    text: 'Disability',
  },
  {
    id: "children's",
    text: "Children's",
  },
];


const SocialSecurityBenefitList = (props) => {
  const TITLE_FALLBACK = 'Please Select';
  const { children: removeButton, id, expanded } = props;
  const [title, setTitle] = useState(TITLE_FALLBACK);
  const [clientId, setClientId] = useState(0);
  const { householdResource } = useSimpleHousehold();

  const isClientFound = () => {
    if (householdResource) {
      return householdResource.attr('clients').resources().list()
        .find(clientResource => clientResource.id() === clientId);
    }
    return false;
  };
  const getClientNameForTitle = () => {
    if (householdResource) {
      const clientsWithId = householdResource.attr('clients').resources().list()
        .filter(clientResource => clientResource.id() === clientId);
      if (clientsWithId.length > 0) {
        return `${clientsWithId[0].attr('firstName')} ${clientsWithId[0].attr('lastName')}`;
      }
    }
    return TITLE_FALLBACK;
  };

  const getRecipientOptions = () => {
    const result = [{
      id: '0',
      value: TITLE_FALLBACK,
    }];
    if (householdResource) {
      result.push(...householdResource?.attr('clients').resources().list().map((clientResource) => {
        return {
          id: clientResource.id(),
          value: `${clientResource.attr('firstName')} ${clientResource.attr('lastName')}`,
        };
      }));
    }
    return result;
  };

  const onChange = (_, value) => {
    setClientId(Number(value));
  };

  useEffect(() => {
    setTitle(getClientNameForTitle());
  }, [clientId]);
  useEffect(() => {
    setTitle(getClientNameForTitle());
  }, [householdResource]);

  return (
    <LabeledVerticalExpander
      transitionSpeed={0.15}
      title={title}
      removeButton={removeButton}
      firstElementFocusable
      expanded={expanded}
    >
      <div>
        <SelectInput
          label="Benefit recipient"
          name={`socialSecurityBenefitRecipient.${id}`}
          answerKey="socialSecurityBenefitRecipient"
          options={getRecipientOptions()}
          onChange={(name, value) => onChange(name, value)}
          errorTooltipText={isClientFound() ? '' : "This benefit was previously assigned to an individual no longer associated with this household. Please review to see how you'd like to handle this benefit."}
        />
      </div>
      <BoolInput
        label="Are you receiving income?"
        name={`socialSecurityBenefitReceivingIncome.${id}`}
        answerKey="socialSecurityBenefitReceivingIncome"
        checkedChildren={
          <Fragment>
            <CurrencyInput
              key="socialSecurityBenefitMonthlyIncome"
              placeholder="Monthly Income"
              name={`socialSecurityBenefitMonthlyIncome.${id}`}
              answerKey="socialSecurityBenefitMonthlyIncome"
            />
            <RadioInput
              className="ss-benefit-type"
              name={`socialSecurityBenefitType.${id}`}
              answerKey="socialSecurityBenefitType"
              options={BENEFIT_TYPE}
            />
          </Fragment>
        }
        uncheckedChildren={
          <Fragment>
            <CurrencyInput
              key="socialSecurityBenefitEstimatedBenefitsAge62"
              placeholder="Estimated benefits: age 62"
              name={`socialSecurityBenefitEstimatedBenefitsAge62.${id}`}
              answerKey="socialSecurityBenefitEstimatedBenefitsAge62"
            />
            <CurrencyInput
              key="socialSecurityBenefitEstimatedBenefitsFullRetirement"
              placeholder="Estimated benefits: full retirement"
              name={`socialSecurityBenefitEstimatedBenefitsFullRetirement.${id}`}
              answerKey="socialSecurityBenefitEstimatedBenefitsFullRetirement"
            />
            <CurrencyInput
              key="socialSecurityBenefitEstimatedBenefitsAge70"
              placeholder="Estimated benefits: age 70"
              name={`socialSecurityBenefitEstimatedBenefitsAge70.${id}`}
              answerKey="socialSecurityBenefitEstimatedBenefitsAge70"
            />
          </Fragment>
        }
      />
    </LabeledVerticalExpander>
  );
};

SocialSecurityBenefitList.propTypes = {
  id: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
  expanded: PropTypes.bool,
};

SocialSecurityBenefitList.defaultProps = {
  expanded: false,
};

const SocialSecurityBenefits = () => {
  const [count, setCount] = useState(0);

  return (
    <Section
      transitionSpeed={0.25}
      height={400}
      title="Social Security Benefits"
      sectionKey={SectionKey}
      expanded={QueryVal(section) === SectionKey}
      CountComponent={<CircleWithCount count={count} className="expander-header-count expander-header-left" />}
    >
      <FieldList
        onCountUpdate={setCount}
        addLabel="Add a SS benefit"
        label="Social security benefits"
        keyPrefix="socialSecurityBenefits"
        Of={SocialSecurityBenefitList}
      />
    </Section>
  );
};

export default SocialSecurityBenefits;
